import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import ar from './locales/ar.json';
import en from './locales/en.json';
import { collectFragments, mergeMessages, type LocaleFragment, type Messages } from './fragments';
import type { Direction, Locale } from '@/shared/types/api';

export const SUPPORTED_LOCALES = ['ar', 'en'] as const;

/** Fidelen is Arabic-first: Arabic is the default, not the alternative. */
export const DEFAULT_LOCALE: Locale = 'ar';

export const LOCALE_META: Record<Locale, { name: string; dir: Direction }> = {
  ar: { name: 'العربية', dir: 'rtl' },
  en: { name: 'English', dir: 'ltr' },
};

export const LOCALE_STORAGE_KEY = 'fidelen.locale';

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export function directionFor(locale: Locale): Direction {
  return LOCALE_META[locale].dir;
}

/**
 * Applies the locale to the document.
 *
 * `dir` on <html> is what actually flips the layout: Tailwind's logical
 * properties (ms-*, pe-*, text-start) all derive from it, so no component needs
 * to branch on language itself.
 */
export function applyDocumentLocale(locale: Locale): void {
  const root = document.documentElement;
  root.lang = locale;
  root.dir = directionFor(locale);
}

/*
 * CONTRACT §6 — locale fragments: a feature area's or the guidance module's
 * own `locales/{en,ar}.json`, deep-merged over the global dictionaries before
 * init, so parallel writers never edit the same file. Literal globs (Vite
 * resolves them at build time), eager, so every string exists before the first
 * render. The fragment parity test lists any key that overrides a global one.
 */
export const LOCALE_FRAGMENTS: readonly LocaleFragment[] = collectFragments({
  ...import.meta.glob<Messages>('/src/features/*/locales/*.json', {
    eager: true,
    import: 'default',
  }),
  // A sub-area's own fragment: src/features/<area>/<sub-area>/locales/{en,ar}.json (platform/leads).
  ...import.meta.glob<Messages>('/src/features/*/*/locales/*.json', {
    eager: true,
    import: 'default',
  }),
  ...import.meta.glob<Messages>('/src/guidance/locales/*.json', {
    eager: true,
    import: 'default',
  }),
  ...import.meta.glob<Messages>('/src/guidance/catalogue/tasks/locales/*.json', {
    eager: true,
    import: 'default',
  }),
  ...import.meta.glob<Messages>('/src/guidance/catalogue/coaches/locales/*.json', {
    eager: true,
    import: 'default',
  }),
  // Shared components' own strings (PUX UI-8): src/shared/locales/<area>/{en,ar}.json.
  ...import.meta.glob<Messages>('/src/shared/locales/*/*.json', {
    eager: true,
    import: 'default',
  }),
});

function withFragments(global: Messages, locale: Locale): Messages {
  let merged = global;

  for (const fragment of LOCALE_FRAGMENTS) {
    if (fragment.locale === locale) merged = mergeMessages(merged, fragment.messages);
  }

  return merged;
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      ar: { translation: withFragments(ar, 'ar') },
      en: { translation: withFragments(en, 'en') },
    },
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    detection: {
      order: ['localStorage', 'htmlTag', 'navigator'],
      lookupLocalStorage: LOCALE_STORAGE_KEY,
      caches: ['localStorage'],
    },
    interpolation: {
      // React escapes for us; double escaping mangles Arabic punctuation.
      escapeValue: false,
    },
    returnNull: false,
  });

i18n.on('languageChanged', (next) => {
  if (isLocale(next)) applyDocumentLocale(next);
});

/*
 * Apply the detected language once, immediately, at module load.
 *
 * `languageChanged` fires on a CHANGE. The detector resolves the stored
 * language synchronously during `init`, so a reader whose stored language
 * differs from the `lang` the document was served with gets no event at all —
 * and `document.documentElement.lang` is what the API client reads to build
 * `X-Fidelen-Locale`.
 *
 * The symptom was quiet and specific: open the product in English and the
 * organisation name in the header came back Arabic, because the session request
 * went out before anything had corrected the document. Labels fetched later —
 * report titles, enum labels — were right, which is what made it look like a
 * data problem rather than a timing one.
 */
if (isLocale(i18n.language)) applyDocumentLocale(i18n.language);

export default i18n;
